import { Typography } from '@mui/material'
import { intl } from '../../../../../../i18n'
import { SearchItemLabelHorizontalContainer } from '../../../../../components/search-forms/search-item-label-container'
import { BlankableDateTermConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/BlankableDateTermConditionItemLabel'
import { DateTimeTermConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/DateTimeTermConditionItemLabel'
import { TextConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/TextConditionItemLabel'
import { ProjectMemberAutocompleteConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/ProjectMemberAutocompleteConditionItemLabel'
import { SprintAutocompleteConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/SprintAutocompleteConditionItemLabel'
import { TeamAutocompleteConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/TeamAutocompleteConditionItemLabel'
import { TicketTypeCheckboxSelectConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/TicketTypeCheckboxSelectConditionItemLabel'
import { WbsItemPriorityCheckboxSelectConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/WbsItemPriorityCheckboxSelectConditionItemLabel'
import { WbsItemStatusCheckboxSelectConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/WbsItemStatusCheckboxSelectConditionItemLabel'
import { WbsItemTypeCheckboxSelectConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/WbsItemTypeCheckboxSelectConditionItemLabel'
import { WbsItemSearchSearchCondition } from '../../../hooks/useSearchCondition'
import { useOnSingleValueChangedGenerator } from './useOnSingleValueChangedGenerator'
import { useMemo } from 'react'
import { colorPalette } from '../../../../../style/colorPalette'
import { TagMultiAutocompleteConditionItemLabel } from '../../../../../components/search-forms/search-item-labels/wbs-item-search-item-labels/TagMultiAutocompleteConditionItemLabel'

type WbsItemSearchSearchItemLabelsProps = {
  searchCondition: WbsItemSearchSearchCondition
  isLoading: boolean
  onChange: (updated: Partial<WbsItemSearchSearchCondition>) => void
  search: () => void
  onClose: () => void
}

export const WbsItemSearchSearchItemLabels = ({
  searchCondition,
  isLoading,
  onChange,
  search,
  onClose,
}: WbsItemSearchSearchItemLabelsProps) => {
  const { onChangeValueGenerator, onChangeBlankGenerator } =
    useOnSingleValueChangedGenerator(searchCondition, onChange)

  const noSearchCondition = useMemo(() => {
    return Object.values(searchCondition)
      .map(v => {
        if (typeof v === 'undefined') {
          return true
        }
        if (Array.isArray(v)) {
          return v.length === 0
        }
        return !v
      })
      .every(v => v)
  }, [searchCondition])

  return (
    <SearchItemLabelHorizontalContainer>
      {noSearchCondition ? (
        <Typography
          sx={{
            fontSize: '14px',
            color: colorPalette.monotone[5],
            fontWeight: 500,
            marginLeft: '10px',
          }}
        >
          {intl.formatMessage({ id: 'wbs.search.no.search.condition' })}
        </Typography>
      ) : (
        <>
          <TextConditionItemLabel
            header={intl.formatMessage({ id: 'wbsItemSearchFilterPanel.code' })}
            value={searchCondition.code}
            onChange={onChangeValueGenerator('code')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <WbsItemTypeCheckboxSelectConditionItemLabel
            value={searchCondition.types}
            onChange={onChangeValueGenerator('types')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <TicketTypeCheckboxSelectConditionItemLabel
            value={searchCondition.ticketTypes}
            onChange={onChangeValueGenerator('ticketTypes')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <WbsItemStatusCheckboxSelectConditionItemLabel
            value={searchCondition.status}
            onChange={onChangeValueGenerator('status')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <WbsItemPriorityCheckboxSelectConditionItemLabel
            value={searchCondition.priority}
            onChange={onChangeValueGenerator('priority')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <TextConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.displayName',
            })}
            value={searchCondition.displayName}
            onChange={onChangeValueGenerator('displayName')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <TextConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.keyword',
            })}
            value={searchCondition.searchText}
            onChange={onChangeValueGenerator('searchText')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <TextConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.description',
            })}
            value={searchCondition.description}
            onChange={onChangeValueGenerator('description')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <TeamAutocompleteConditionItemLabel
            value={searchCondition.teamUuid}
            onChange={onChangeValueGenerator('teamUuid')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <SprintAutocompleteConditionItemLabel
            value={searchCondition.sprintUuid}
            teamUuid={searchCondition.teamUuid}
            onChange={onChangeValueGenerator('sprintUuid')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <ProjectMemberAutocompleteConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.accountable',
            })}
            value={searchCondition.accountableUuid}
            onChange={onChangeValueGenerator('accountableUuid')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <ProjectMemberAutocompleteConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.responsible',
            })}
            value={searchCondition.responsibleUuid}
            onChange={onChangeValueGenerator('responsibleUuid')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <ProjectMemberAutocompleteConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.assignee',
            })}
            value={searchCondition.assigneeUuid}
            onChange={onChangeValueGenerator('assigneeUuid')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <TagMultiAutocompleteConditionItemLabel
            value={searchCondition.tagUuids}
            onChange={onChangeValueGenerator('tagUuids')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <BlankableDateTermConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.scheduledStartDate',
            })}
            value={searchCondition.scheduledStartDate}
            onChange={onChangeValueGenerator('scheduledStartDate')}
            onClose={onClose}
            search={search}
            blank={!!searchCondition.blank?.scheduledStartDate}
            onChangeBlank={onChangeBlankGenerator('scheduledStartDate')}
            isLoading={isLoading}
          />
          <BlankableDateTermConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.scheduledEndDate',
            })}
            value={searchCondition.scheduledEndDate}
            onChange={onChangeValueGenerator('scheduledEndDate')}
            onClose={onClose}
            search={search}
            blank={!!searchCondition.blank?.scheduledEndDate}
            onChangeBlank={onChangeBlankGenerator('scheduledEndDate')}
            isLoading={isLoading}
          />
          <BlankableDateTermConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.actualStartDate',
            })}
            value={searchCondition.actualStartDate}
            onChange={onChangeValueGenerator('actualStartDate')}
            onClose={onClose}
            search={search}
            blank={!!searchCondition.blank?.actualStartDate}
            onChangeBlank={onChangeBlankGenerator('actualStartDate')}
            isLoading={isLoading}
          />
          <BlankableDateTermConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.actualEndDate',
            })}
            value={searchCondition.actualEndDate}
            onChange={onChangeValueGenerator('actualEndDate')}
            onClose={onClose}
            search={search}
            blank={!!searchCondition.blank?.actualEndDate}
            onChangeBlank={onChangeBlankGenerator('actualEndDate')}
            isLoading={isLoading}
          />
          <DateTimeTermConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.createdAt',
            })}
            value={searchCondition.createdAt}
            onChange={onChangeValueGenerator('createdAt')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <DateTimeTermConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.updatedAt',
            })}
            value={searchCondition.updatedAt}
            onChange={onChangeValueGenerator('updatedAt')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <ProjectMemberAutocompleteConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.createdBy',
            })}
            value={searchCondition.createdByUuid}
            onChange={onChangeValueGenerator('createdByUuid')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
          <ProjectMemberAutocompleteConditionItemLabel
            header={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.updatedBy',
            })}
            value={searchCondition.updatedByUuid}
            onChange={onChangeValueGenerator('updatedByUuid')}
            onClose={onClose}
            search={search}
            isLoading={isLoading}
          />
        </>
      )}
    </SearchItemLabelHorizontalContainer>
  )
}
